import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { EmptyState, getScoreNum, fmtScore } from './shared';

const GRAD = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const sansation = { fontFamily: "'Sansation', sans-serif" };

const SlideAnalysis = ({
  slidesData = [],
  isLoadingHtml = false,
  onSlideChange,
}) => {
  const [slideView, setSlideView] = useState('grid');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goTo = (index) => {
    const clamped = Math.max(0, Math.min(slidesData.length - 1, index));
    setActiveIndex(clamped);
    onSlideChange?.(clamped);
  };

  const openDetail = (index) => {
    const clamped = Math.max(0, Math.min(slidesData.length - 1, index));
    setActiveIndex(clamped);
    setSlideView('detail');
    onSlideChange?.(clamped);
  };

  const slide = slidesData[activeIndex];

  const getScoreColor = (n) => n >= 70 ? '#1b8c2e' : n >= 50 ? '#9A6700' : '#d12b2b';
  const getScoreBg    = (n) => n >= 70 ? 'rgba(27,140,46,0.07)' : n >= 50 ? 'rgba(154,103,0,0.07)' : 'rgba(209,43,43,0.06)';

  return (
    <div style={{ fontFamily: 'inherit', width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>

      {/* Title row */}
      <div style={{ 
        display: 'flex', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        justifyContent: 'space-between', 
        marginBottom: isMobile ? 14 : 20,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 10 : 0,
      }}>
        <h2 style={{ 
          ...sansation, 
          fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
          fontWeight: 700, 
          margin: 0, 
          color: '#0f172a', 
          letterSpacing: '-0.02em' 
        }}>
          Slide-by-Slide Analysis
        </h2>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: '#f8f8fa', 
          border: '0.5px solid rgba(0,0,0,0.06)', 
          borderRadius: isMobile ? 6 : 8, 
          padding: 2, 
          gap: 2,
          width: isMobile ? '100%' : 'auto',
        }}>
          {[{ k: 'grid', l: 'Grid', ic: 'LayoutGrid' }, { k: 'detail', l: 'Detail', ic: 'Eye' }].map(b => (
            <button key={b.k}
              onClick={() => setSlideView(b.k)}
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: isMobile ? 4 : 6, 
                padding: isMobile ? '5px 10px' : '5px 14px', 
                borderRadius: 6, 
                border: 'none', 
                cursor: 'pointer', 
                fontFamily: 'inherit', 
                fontSize: isMobile ? 10 : 11, 
                fontWeight: 600, 
                transition: 'all 0.12s',
                flex: isMobile ? 1 : 'none',
                justifyContent: isMobile ? 'center' : 'flex-start',
                ...(slideView === b.k ? { 
                  background: '#ffffff', 
                  color: '#0f172a', 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)' 
                } : { 
                  background: 'transparent', 
                  color: '#aeaeb2' 
                }) 
              }}>
              <Icon name={b.ic} size={isMobile ? 12 : 13} /> {b.l}
            </button>
          ))}
        </div>
      </div>

      <div style={{ 
        background: '#ffffff', 
        borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
        border: '0.5px solid rgba(0,0,0,0.06)', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
        padding: isMobile ? '16px 14px' : (isTablet ? '20px 18px' : 24),
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}>
        {isLoadingHtml ? (
          <div style={{ padding: isMobile ? '32px 0' : '48px 0', textAlign: 'center' }}>
            <p style={{ fontFamily: 'inherit', fontSize: isMobile ? 12 : 13, color: '#6e6e73' }}>Loading slides...</p>
          </div>

        ) : slideView === 'grid' ? (
          slidesData.length > 0 ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)'), 
              gap: isMobile ? 10 : 12,
              width: '100%',
            }}>
              {slidesData.map((s, index) => {
                const n = getScoreNum(s.slider_score);
                const sc = n >= 70 ? '#30d158' : n >= 50 ? '#ff9f0a' : '#ff453a';
                const sb = n >= 70 ? 'rgba(48,209,88,0.15)' : n >= 50 ? 'rgba(255,159,10,0.15)' : 'rgba(255,69,58,0.15)';
                return (
                  <div key={index}
                    onClick={() => openDetail(index)}
                    style={{ 
                      background: '#ffffff', 
                      border: '0.5px solid rgba(0,0,0,0.06)', 
                      borderRadius: isMobile ? 8 : 10, 
                      overflow: 'hidden', 
                      cursor: 'pointer', 
                      transition: 'all 0.15s',
                      width: '100%',
                    }}
                    onMouseEnter={e => { 
                      e.currentTarget.style.borderColor = '#004AAD'; 
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,74,173,0.12)'; 
                    }}
                    onMouseLeave={e => { 
                      e.currentTarget.style.borderColor = 'rgba(0,0,0,0.06)'; 
                      e.currentTarget.style.boxShadow = 'none'; 
                    }}
                  >
                    <div style={{ 
                      padding: isMobile ? '14px 12px' : '16px 14px', 
                      background: GRAD, 
                      color: 'white', 
                      minHeight: isMobile ? 70 : 90, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'flex-end', 
                      position: 'relative' 
                    }}>
                      <div style={{ 
                        position: 'absolute', 
                        top: isMobile ? 6 : 8, 
                        right: isMobile ? 6 : 8, 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 9 : 10, 
                        fontWeight: 700, 
                        padding: '2px 8px', 
                        borderRadius: 5, 
                        background: sb, 
                        color: sc 
                      }}>
                        {n > 0 ? `${n}/100` : '—'}
                      </div>
                      <div style={{ 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 11 : 12, 
                        fontWeight: 700, 
                        lineHeight: 1.35, 
                        marginBottom: 3,
                        wordBreak: 'break-word',
                      }}>
                        {s.slider_name || `Slide ${s.slider_sr_no}`}
                      </div>
                      <div style={{ 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 9 : 10, 
                        opacity: 0.5 
                      }}>
                        {s.slider_type || ''}
                      </div>
                    </div>
                    <div style={{ padding: isMobile ? '8px 12px' : '10px 14px' }}>
                      <div style={{ 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 8 : 9, 
                        color: '#aeaeb2', 
                        marginBottom: 2 
                      }}>
                        Slide {String(s.slider_sr_no).padStart(2, '0')}
                      </div>
                      <div style={{ 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 10 : 11, 
                        fontWeight: 600, 
                        color: '#0f172a', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        marginBottom: 6 
                      }}>
                        {s.slider_name || `Slide ${s.slider_sr_no}`}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: isMobile ? 6 : 8, 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 9 : 10 
                      }}>
                        <span style={{ color: '#1b8c2e' }}>✓ {s.solutions?.length || 0}</span>
                        <span style={{ color: '#d12b2b' }}>✕ {s.issues?.length || 0}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <EmptyState icon="Presentation" title="No slide data" desc="Slide data will populate from your report." />

        ) : (
          /* ── DETAIL VIEW ── */
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr' : '1fr 260px'), 
            gap: isMobile ? 16 : 20,
            width: '100%',
          }}>

            {/* LEFT / TOP */}
            <div style={{ minWidth: 0 }}>
              {slide ? (
                <>
                  {/* Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'space-between', 
                    marginBottom: isMobile ? 14 : 20,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 8 : 0,
                  }}>
                    <div style={{ minWidth: 0, width: '100%' }}>
                      <div style={{ 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 10 : 11, 
                        color: '#aeaeb2', 
                        marginBottom: 4 
                      }}>
                        Slide {String(slide.slider_sr_no).padStart(2, '0')}
                      </div>
                      <h3 style={{ 
                        ...sansation, 
                        fontSize: isMobile ? 15 : (isTablet ? 16 : 17), 
                        fontWeight: 700, 
                        margin: 0, 
                        color: '#0f172a',
                        wordBreak: 'break-word',
                      }}>
                        {slide.slider_name}
                        {slide.slider_type && (
                          <span style={{ 
                            fontFamily: 'inherit', 
                            fontSize: isMobile ? 12 : 13, 
                            fontWeight: 400, 
                            color: '#6e6e73', 
                            marginLeft: isMobile ? 4 : 8,
                            display: isMobile ? 'block' : 'inline',
                            marginTop: isMobile ? 4 : 0,
                          }}>
                            {isMobile ? slide.slider_type : `— ${slide.slider_type}`}
                          </span>
                        )}
                      </h3>
                    </div>
                    {(() => {
                      const n = getScoreNum(slide.slider_score);
                      return n > 0 ? (
                        <span style={{ 
                          fontFamily: 'inherit', 
                          fontSize: isMobile ? 12 : 13, 
                          fontWeight: 700, 
                          padding: isMobile ? '3px 12px' : '4px 14px', 
                          borderRadius: isMobile ? 6 : 8, 
                          background: getScoreBg(n), 
                          color: getScoreColor(n), 
                          flexShrink: 0,
                          alignSelf: isMobile ? 'flex-start' : 'flex-start',
                        }}>
                          {fmtScore(slide.slider_score)}
                        </span>
                      ) : null;
                    })()}
                  </div>

                  {/* Cards */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                    gap: isMobile ? 10 : 12,
                    width: '100%',
                  }}>
                    <div style={{ 
                      background: 'rgba(27,140,46,0.07)', 
                      padding: isMobile ? 14 : 18, 
                      borderRadius: isMobile ? 10 : 14,
                      minWidth: 0,
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: isMobile ? 6 : 8, 
                        marginBottom: isMobile ? 10 : 14 
                      }}>
                        <Icon name="CheckCircle" size={isMobile ? 14 : 16} color="#1b8c2e" />
                        <span style={{ 
                          ...sansation, 
                          fontSize: isMobile ? 12 : 13, 
                          fontWeight: 700, 
                          color: '#0f172a' 
                        }}>
                          What Works ({slide.solutions?.length || 0})
                        </span>
                      </div>
                      {slide.solutions?.length > 0
                        ? slide.solutions.map((s, i) => (
                          <div key={i} style={{ 
                            display: 'flex', 
                            gap: isMobile ? 6 : 8, 
                            alignItems: 'flex-start', 
                            marginBottom: isMobile ? 6 : 8 
                          }}>
                            <span style={{ 
                              width: isMobile ? 4 : 5, 
                              height: isMobile ? 4 : 5, 
                              borderRadius: '50%', 
                              background: '#1b8c2e', 
                              flexShrink: 0, 
                              marginTop: isMobile ? 6 : 7 
                            }} />
                            <span style={{ 
                              fontFamily: 'inherit', 
                              fontSize: isMobile ? 12 : 13, 
                              color: '#374151', 
                              lineHeight: 1.6,
                              wordBreak: 'break-word',
                            }}>
                              {s}
                            </span>
                          </div>
                        ))
                        : <p style={{ 
                          fontFamily: 'inherit', 
                          fontSize: isMobile ? 11 : 12, 
                          color: '#aeaeb2' 
                        }}>
                          No strengths listed.
                        </p>}
                    </div>

                    <div style={{ 
                      background: 'rgba(209,43,43,0.06)', 
                      padding: isMobile ? 14 : 18, 
                      borderRadius: isMobile ? 10 : 14,
                      minWidth: 0,
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: isMobile ? 6 : 8, 
                        marginBottom: isMobile ? 10 : 14 
                      }}>
                        <Icon name="AlertTriangle" size={isMobile ? 14 : 16} color="#d12b2b" />
                        <span style={{ 
                          ...sansation, 
                          fontSize: isMobile ? 12 : 13, 
                          fontWeight: 700, 
                          color: '#0f172a' 
                        }}>
                          What Doesn't Work ({slide.issues?.length || 0})
                        </span>
                      </div>
                      {slide.issues?.length > 0
                        ? slide.issues.map((issue, i) => (
                          <div key={i} style={{ 
                            display: 'flex', 
                            gap: isMobile ? 6 : 8, 
                            alignItems: 'flex-start', 
                            marginBottom: isMobile ? 6 : 8 
                          }}>
                            <span style={{ 
                              width: isMobile ? 4 : 5, 
                              height: isMobile ? 4 : 5, 
                              borderRadius: '50%', 
                              background: '#d12b2b', 
                              flexShrink: 0, 
                              marginTop: isMobile ? 6 : 7 
                            }} />
                            <span style={{ 
                              fontFamily: 'inherit', 
                              fontSize: isMobile ? 12 : 13, 
                              color: '#374151', 
                              lineHeight: 1.6,
                              wordBreak: 'break-word',
                            }}>
                              {issue}
                            </span>
                          </div>
                        ))
                        : <p style={{ 
                          fontFamily: 'inherit', 
                          fontSize: isMobile ? 11 : 12, 
                          color: '#aeaeb2' 
                        }}>
                          No issues found.
                        </p>}
                    </div>
                  </div>

                  {/* Prev / Next */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    marginTop: isMobile ? 14 : 20,
                    gap: isMobile ? 8 : 0,
                  }}>
                    <button
                      onClick={() => goTo(activeIndex - 1)}
                      disabled={activeIndex === 0}
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: isMobile ? 4 : 6, 
                        padding: isMobile ? '5px 12px' : '6px 16px', 
                        borderRadius: isMobile ? 6 : 8, 
                        border: '0.5px solid rgba(0,0,0,0.1)', 
                        background: '#ffffff', 
                        color: '#0f172a', 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 11 : 12, 
                        fontWeight: 600, 
                        cursor: activeIndex === 0 ? 'not-allowed' : 'pointer', 
                        opacity: activeIndex === 0 ? 0.3 : 1 
                      }}>
                      <Icon name="ChevronLeft" size={isMobile ? 13 : 15} /> 
                      {!isMobile && 'Previous'}
                    </button>
                    <span style={{ 
                      fontFamily: 'inherit', 
                      fontSize: isMobile ? 11 : 12, 
                      color: '#aeaeb2',
                      flexShrink: 0,
                    }}>
                      {activeIndex + 1} / {slidesData.length}
                    </span>
                    <button
                      onClick={() => goTo(activeIndex + 1)}
                      disabled={activeIndex >= slidesData.length - 1}
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: isMobile ? 4 : 6, 
                        padding: isMobile ? '5px 12px' : '6px 16px', 
                        borderRadius: isMobile ? 6 : 8, 
                        border: '0.5px solid rgba(0,0,0,0.1)', 
                        background: '#ffffff', 
                        color: '#0f172a', 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 11 : 12, 
                        fontWeight: 600, 
                        cursor: activeIndex >= slidesData.length - 1 ? 'not-allowed' : 'pointer', 
                        opacity: activeIndex >= slidesData.length - 1 ? 0.3 : 1 
                      }}>
                      {!isMobile && 'Next'} <Icon name="ChevronRight" size={isMobile ? 13 : 15} />
                    </button>
                  </div>
                </>
              ) : (
                <EmptyState icon="Presentation" title="No slide selected" desc="Click a slide from the list." />
              )}
            </div>

            {/* RIGHT — slide list (hidden on mobile in detail view) */}
            {!isMobile && (
              <div style={{ 
                background: '#ffffff', 
                border: '0.5px solid rgba(0,0,0,0.06)', 
                borderRadius: 16, 
                overflow: 'hidden', 
                alignSelf: 'start' 
              }}>
                <div style={{ 
                  padding: '14px 16px', 
                  borderBottom: '0.5px solid rgba(0,0,0,0.06)', 
                  background: '#f8f8fa' 
                }}>
                  <span style={{ 
                    fontFamily: 'inherit', 
                    fontSize: 13, 
                    fontWeight: 700, 
                    color: '#0f172a' 
                  }}>
                    All Slides ({slidesData.length})
                  </span>
                </div>
                <div style={{ overflowY: 'auto', maxHeight: 520 }}>
                  {slidesData.map((s, index) => {
                    const n = getScoreNum(s.slider_score);
                    const isActive = index === activeIndex;
                    return (
                      <div key={index}
                        onClick={() => goTo(index)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 10, 
                          padding: '8px 12px', 
                          borderBottom: '0.5px solid rgba(0,0,0,0.06)', 
                          borderLeft: isActive ? '3px solid #004AAD' : '3px solid transparent', 
                          background: isActive ? 'rgba(0,74,173,0.07)' : 'transparent', 
                          cursor: 'pointer', 
                          transition: 'background 0.12s' 
                        }}>
                        <div style={{ 
                          width: 36, 
                          height: 24, 
                          borderRadius: 4, 
                          background: GRAD, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          flexShrink: 0 
                        }}>
                          <span style={{ 
                            fontFamily: 'inherit', 
                            fontSize: 7, 
                            fontWeight: 600, 
                            color: 'rgba(255,255,255,0.7)' 
                          }}>
                            {String(s.slider_sr_no).padStart(2, '0')}
                          </span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontFamily: 'inherit', 
                            fontSize: 11, 
                            fontWeight: 600, 
                            color: isActive ? '#004AAD' : '#0f172a', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            lineHeight: 1.3 
                          }}>
                            {s.slider_name || `Slide ${s.slider_sr_no}`}
                          </div>
                          <div style={{ 
                            fontFamily: 'inherit', 
                            fontSize: 9, 
                            color: '#aeaeb2', 
                            marginTop: 2 
                          }}>
                            Slide {String(s.slider_sr_no).padStart(2, '0')}
                          </div>
                        </div>
                        {n > 0 && (
                          <span style={{ 
                            fontFamily: 'inherit', 
                            fontSize: 10, 
                            fontWeight: 700, 
                            color: getScoreColor(n), 
                            flexShrink: 0 
                          }}>
                            {n}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default SlideAnalysis;